import { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

import { llmPlatformAPI, type LlmChannel } from '../../../baselines/llm/llmPlatformAPI';
import toast from '../../Toast';

const PROVIDER_OPTIONS = [
  { value: 'openai-compat', label: 'OpenAI 兼容（默认）' },
  { value: 'openai-reasoning', label: 'OpenAI 推理模型' },
  { value: 'deepseek-r1', label: 'DeepSeek-R1（剥离 reasoning）' },
  { value: 'anthropic', label: 'Anthropic 原生（未实现）' },
  { value: 'gemini', label: 'Gemini 原生（未实现）' },
];

interface FormState {
  id?: string;
  name: string;
  provider: string;
  apiUrl: string;
  apiKey: string;
  isActive: boolean;
  sortOrder: number;
  remark: string;
  defaultTotalTimeoutMs: number;
  defaultIdleTimeoutMs: number;
}

const EMPTY_FORM: FormState = {
  name: '',
  provider: 'openai-compat',
  apiUrl: '',
  apiKey: '',
  isActive: true,
  sortOrder: 0,
  remark: '',
  defaultTotalTimeoutMs: 180000,
  defaultIdleTimeoutMs: 30000,
};

export default function LlmChannelsTab() {
  const [channels, setChannels] = useState<LlmChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await llmPlatformAPI.listChannels();
      setChannels(res.data);
    } catch (err) {
      toast.error(`加载失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onCreate = () => setEditing({ ...EMPTY_FORM });

  const onEdit = (c: LlmChannel) => {
    setEditing({
      id: c.id,
      name: c.name,
      provider: c.provider,
      apiUrl: c.apiUrl,
      apiKey: '', // 留空表示不变（mask 行为由后端 enforce）
      isActive: c.isActive,
      sortOrder: c.sortOrder,
      remark: c.remark ?? '',
      defaultTotalTimeoutMs: c.defaultTotalTimeoutMs,
      defaultIdleTimeoutMs: c.defaultIdleTimeoutMs,
    });
  };

  const onDelete = async (c: LlmChannel) => {
    if (!confirm(`确定删除渠道 ${c.name}？`)) return;
    try {
      await llmPlatformAPI.deleteChannel(c.id);
      toast.success('已删除');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  const onSubmit = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.apiUrl.trim()) {
      toast.error('请填写名称和 apiUrl');
      return;
    }
    if (!editing.id && !editing.apiKey.trim()) {
      toast.error('新建时必须填写 apiKey');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Partial<LlmChannel> = {
        name: editing.name.trim(),
        provider: editing.provider,
        apiUrl: editing.apiUrl.trim(),
        isActive: editing.isActive,
        sortOrder: editing.sortOrder,
        remark: editing.remark.trim() || null,
        defaultTotalTimeoutMs: editing.defaultTotalTimeoutMs,
        defaultIdleTimeoutMs: editing.defaultIdleTimeoutMs,
      };
      // apiKey：仅在用户填了非空且非 mask 时才提交（编辑时留空 = 不变）
      if (editing.apiKey.trim() && !editing.apiKey.endsWith('****')) {
        payload.apiKey = editing.apiKey.trim();
      }
      if (editing.id) {
        await llmPlatformAPI.updateChannel(editing.id, payload);
        toast.success('已更新');
      } else {
        await llmPlatformAPI.createChannel(payload);
        toast.success('已创建');
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold text-light-text">LLM 渠道</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-light-border hover:bg-light-hover"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-accent-blue text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            新增渠道
          </button>
        </div>
      </div>

      {loading && channels.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-light-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> 加载中
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-light-border overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-light-hover text-light-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">名称</th>
                  <th className="px-4 py-3 text-left font-medium">Provider</th>
                  <th className="px-4 py-3 text-left font-medium">apiUrl</th>
                  <th className="px-4 py-3 text-left font-medium">apiKey</th>
                  <th className="px-4 py-3 text-left font-medium">关联模型</th>
                  <th className="px-4 py-3 text-left font-medium">状态</th>
                  <th className="px-4 py-3 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {channels.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-light-muted">
                      暂无渠道，点击右上角"新增渠道"创建第一条
                    </td>
                  </tr>
                )}
                {channels.map((c) => (
                  <tr key={c.id} className="border-t border-light-border">
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3">{c.provider}</td>
                    <td className="px-4 py-3 text-xs text-light-muted truncate max-w-[260px]">{c.apiUrl}</td>
                    <td className="px-4 py-3 text-xs font-mono">{c.apiKey}</td>
                    <td className="px-4 py-3">{c.modelCount ?? 0}</td>
                    <td className="px-4 py-3">
                      <StatusPill isActive={c.isActive} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChannelActions channel={c} onEdit={onEdit} onDelete={onDelete} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-light-border lg:hidden">
            {channels.length === 0 ? (
              <div className="px-4 py-8 text-center text-light-muted">
                暂无渠道，点击右上角"新增渠道"创建第一条
              </div>
            ) : (
              channels.map((c) => (
                <div key={c.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-light-text">{c.name}</div>
                      <div className="mt-1 text-xs text-light-muted">{c.provider}</div>
                    </div>
                    <StatusPill isActive={c.isActive} />
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <InfoBlock label="apiUrl" value={c.apiUrl} mono />
                    <InfoBlock label="apiKey" value={c.apiKey} mono />
                    <InfoBlock label="关联模型" value={String(c.modelCount ?? 0)} />
                    <InfoBlock label="排序" value={String(c.sortOrder)} />
                  </div>
                  <ChannelActions channel={c} onEdit={onEdit} onDelete={onDelete} mobile />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {editing && (
        <ChannelEditModal
          editing={editing}
          submitting={submitting}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSubmit={() => void onSubmit()}
        />
      )}
    </div>
  );
}

interface ModalProps {
  editing: FormState;
  submitting: boolean;
  onChange: (f: FormState) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function ChannelEditModal({ editing, submitting, onChange, onCancel, onSubmit }: ModalProps) {
  const isEdit = !!editing.id;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? '编辑渠道' : '新增渠道'}</h3>
          <button onClick={onCancel} className="text-light-muted hover:text-light-text">×</button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="名称（唯一）" required>
            <input
              className="input"
              value={editing.name}
              onChange={(e) => onChange({ ...editing, name: e.target.value })}
            />
          </Field>
          <Field label="Provider" required>
            <select
              className="input"
              value={editing.provider}
              onChange={(e) => onChange({ ...editing, provider: e.target.value })}
            >
              {PROVIDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="apiUrl（不含 /chat/completions）" required>
            <input
              className="input"
              placeholder="https://api.deepseek.com"
              value={editing.apiUrl}
              onChange={(e) => onChange({ ...editing, apiUrl: e.target.value })}
            />
          </Field>
          <Field
            label={isEdit ? 'apiKey（留空表示不变）' : 'apiKey'}
            required={!isEdit}
          >
            <input
              type="password"
              className="input font-mono"
              placeholder={isEdit ? '****（已存在；填入新值才会更新）' : 'sk-...'}
              value={editing.apiKey}
              onChange={(e) => onChange({ ...editing, apiKey: e.target.value })}
            />
          </Field>
          <Field label="备注">
            <input
              className="input"
              value={editing.remark}
              onChange={(e) => onChange({ ...editing, remark: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="默认总超时（ms）">
              <input
                type="number"
                className="input"
                value={editing.defaultTotalTimeoutMs}
                onChange={(e) =>
                  onChange({ ...editing, defaultTotalTimeoutMs: Number(e.target.value) || 0 })
                }
              />
            </Field>
            <Field label="默认 idle 超时（ms）">
              <input
                type="number"
                className="input"
                value={editing.defaultIdleTimeoutMs}
                onChange={(e) =>
                  onChange({ ...editing, defaultIdleTimeoutMs: Number(e.target.value) || 0 })
                }
              />
            </Field>
            <Field label="排序">
              <input
                type="number"
                className="input"
                value={editing.sortOrder}
                onChange={(e) =>
                  onChange({ ...editing, sortOrder: Number(e.target.value) || 0 })
                }
              />
            </Field>
            <Field label="启用">
              <label className="inline-flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={editing.isActive}
                  onChange={(e) => onChange({ ...editing, isActive: e.target.checked })}
                />
                <span>启用</span>
              </label>
            </Field>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-light-border flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-light-border hover:bg-light-hover"
          >
            取消
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-accent-blue text-white disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} 保存
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">启用</span>
  ) : (
    <span className="px-2 py-1 text-xs rounded bg-gray-200 text-gray-600">禁用</span>
  );
}

function InfoBlock({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className={`mt-1 break-all text-sm text-light-text ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function ChannelActions({
  channel,
  onEdit,
  onDelete,
  mobile = false,
}: {
  channel: LlmChannel;
  onEdit: (channel: LlmChannel) => void;
  onDelete: (channel: LlmChannel) => Promise<void>;
  mobile?: boolean;
}) {
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'inline-flex items-center'}>
      <button
        onClick={() => onEdit(channel)}
        className={mobile
          ? 'inline-flex items-center gap-1 px-3 py-2 text-xs rounded border border-light-border hover:bg-light-hover'
          : 'inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-light-hover'}
      >
        <Pencil className="w-3.5 h-3.5" /> 编辑
      </button>
      <button
        onClick={() => void onDelete(channel)}
        className={mobile
          ? 'inline-flex items-center gap-1 px-3 py-2 text-xs rounded border border-rose-200 hover:bg-red-50 text-red-600'
          : 'inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-red-600 ml-1'}
      >
        <Trash2 className="w-3.5 h-3.5" /> 删除
      </button>
    </div>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="block text-sm text-light-muted mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
